import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Ban, CheckCircle, Plus, Trash2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function IPTable({ entries, onRemove, emptyMsg }) {
  if (entries.length === 0) return <p className="text-muted-foreground text-sm py-4">{emptyMsg}</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground text-xs uppercase tracking-wide border-b border-border">
          <th className="py-2 px-3">IP Address</th>
          <th className="py-2 px-3">Reason</th>
          <th className="py-2 px-3">Added By</th>
          <th className="py-2 px-3">Expires</th>
          <th className="py-2 px-3"></th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id} className="border-b border-border/50 hover:bg-muted/30">
            <td className="py-2 px-3 font-mono">{e.ip_address}</td>
            <td className="py-2 px-3 text-muted-foreground">{e.reason || '--'}</td>
            <td className="py-2 px-3 text-muted-foreground">{e.added_by || '--'}</td>
            <td className="py-2 px-3 text-muted-foreground">{e.expires_at ? new Date(e.expires_at).toLocaleDateString() : 'Never'}</td>
            <td className="py-2 px-3">
              <button onClick={() => onRemove(e.ip_address, e.list_type)} className="text-red-400 hover:text-red-300 transition-colors">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function IPBlocklistPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('block');
  const [blocked, setBlocked] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formIp, setFormIp] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formExpiry, setFormExpiry] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const [bRes, aRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/fraud/blocklist`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/fraud/allowlist`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (bRes.ok) setBlocked(await bRes.json());
      if (aRes.ok) setAllowed(await aRes.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchLists(); }, []);

  const handleAdd = async () => {
    if (!formIp.trim()) { setFormError('IP address is required.'); return; }
    setSubmitting(true);
    setFormError('');
    try {
      const endpoint = tab === 'block' ? 'blocklist' : 'allowlist';
      const res = await fetch(`${API_URL}/api/admin/fraud/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip_address: formIp.trim(), reason: formReason || null, expires_at: formExpiry || null }),
      });
      if (res.ok) {
        setFormIp(''); setFormReason(''); setFormExpiry(''); setShowForm(false);
        await fetchLists();
      } else {
        const data = await res.json();
        setFormError(data.detail || 'Failed to add IP.');
      }
    } catch (e) { setFormError('Network error.'); }
    setSubmitting(false);
  };

  const handleRemove = async (ip, listType) => {
    const endpoint = listType === 'block' ? 'blocklist' : 'allowlist';
    try {
      await fetch(`${API_URL}/api/admin/fraud/${endpoint}/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchLists();
    } catch (e) { /* silent */ }
  };

  return (
    <div className="space-y-6" data-testid="ip-blocklist-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <Ban className="h-6 w-6 text-accent" strokeWidth={1.5} />
            IP Controls
          </h1>
          <p className="text-muted-foreground text-sm">Block or allowlist IP addresses</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="gap-2">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add IP
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-sm">Add IP to {tab === 'block' ? 'Blocklist' : 'Allowlist'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              className="bg-muted border border-border rounded-lg px-3 py-2 text-sm"
              placeholder="IP address (e.g. 1.2.3.4)"
              value={formIp}
              onChange={(e) => setFormIp(e.target.value)}
            />
            <input
              className="bg-muted border border-border rounded-lg px-3 py-2 text-sm"
              placeholder="Reason (optional)"
              value={formReason}
              onChange={(e) => setFormReason(e.target.value)}
            />
            <input
              type="date"
              className="bg-muted border border-border rounded-lg px-3 py-2 text-sm"
              placeholder="Expiry (optional)"
              value={formExpiry}
              onChange={(e) => setFormExpiry(e.target.value)}
            />
          </div>
          {formError && <p className="text-red-400 text-sm">{formError}</p>}
          <div className="flex gap-3">
            <Button size="sm" onClick={handleAdd} disabled={submitting}>
              {submitting ? 'Adding...' : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('block')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'block' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Ban className="h-3.5 w-3.5 inline mr-1.5" strokeWidth={1.5} />
          Blocked ({blocked.length})
        </button>
        <button
          onClick={() => setTab('allow')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'allow' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <CheckCircle className="h-3.5 w-3.5 inline mr-1.5" strokeWidth={1.5} />
          Allowed ({allowed.length})
        </button>
      </div>

      {/* List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden p-5">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-accent border-t-transparent rounded-full" />
          </div>
        ) : tab === 'block' ? (
          <IPTable entries={blocked} onRemove={handleRemove} emptyMsg="No blocked IPs." />
        ) : (
          <IPTable entries={allowed} onRemove={handleRemove} emptyMsg="No allowlisted IPs." />
        )}
      </div>
    </div>
  );
}

